import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, assetUrl } from "@/lib/api";
import { ExploreHeader, TodayHours, RestrictionBadges, ExploreHolding, ExploreError } from "@/attendee/ExploreShared";
import { Input } from "@/components/ui/input";
import { Search, MapPin, UtensilsCrossed, Wine, BookOpenText } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "restaurants", label: "Restaurants" },
  { key: "bars", label: "Bars & Lounges" },
];

export default function ExploreDining() {
  const navigate = useNavigate();
  const { eventId } = useApp();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | error | ok

  const load = useCallback(() => {
    let active = true;
    setState("loading");
    setData(null);
    api.exploreDining(eventId, filter, q)
      .then((d) => { if (active) { setData(d); setState("ok"); } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [eventId, filter, q]);

  useEffect(() => load(), [load]);

  // API/auth failure must not be shown as "no venues".
  if (state === "error") return <ExploreError onRetry={load} />;
  // Not activated/configured => holding screen (also guards direct deep links).
  if (state === "ok" && data && data.activated === false) return <ExploreHolding />;

  const items = data?.items || [];

  return (
    <div className="pt-16 px-4 pb-6">
      <ExploreHeader eyebrow="Explore" title="Dining & Bars" subtitle="Hyatt Ziva Los Cabos" />

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cream-dim" />
        <Input
          data-testid="dining-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search venues or cuisine"
          className="pl-9 bg-[hsl(var(--pc-ink))] border-[hsl(var(--pc-line))] text-cream placeholder:text-cream-dim"
        />
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="dining-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`dining-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === f.key ? "bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))]" : "att-glass text-cream-dim hover:text-cream"}`}
          >
            {f.label}
            {f.key === "restaurants" && data?.counts ? ` (${data.counts.restaurants})` : ""}
            {f.key === "bars" && data?.counts ? ` (${data.counts.bars})` : ""}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-cream-dim mb-3 -mt-1" data-testid="dining-hours-note">Today's service hours · Hotel local time (Los Cabos).</p>

      {!data && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 att-card animate-pulse" />)}</div>}

      {data && items.length === 0 && (
        <div className="att-card p-8 text-center text-sm text-cream-dim">No venues match your search.</div>
      )}

      <div className="space-y-3">
        {items.map((r) => {
          const isMenu = r.subtype === "pool_menu";
          const Icon = isMenu ? BookOpenText : (r.subtype === "bar" ? Wine : UtensilsCrossed);
          return (
            <button
              key={r.id}
              data-testid={`dining-row-${r.id}`}
              onClick={() => !isMenu && navigate(`/app/explore/dining/${r.id}`)}
              className={`w-full att-card overflow-hidden flex text-left ${isMenu ? "cursor-default" : "focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"}`}
            >
              {r.image_url && (
                <img src={assetUrl(r.image_url)} alt="" loading="lazy" decoding="async" className="h-auto w-24 shrink-0 self-stretch object-cover" />
              )}
              <div className="p-3 min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-[hsl(var(--pc-gold))]">
                  <Icon className="h-3.5 w-3.5 shrink-0" /> {isMenu ? "Menu collection" : r.cuisine}
                </div>
                <div className="font-serif text-xl text-cream leading-tight">{r.name}</div>
                {!isMenu && <div className="mt-1"><TodayHours today={r.today} compact /></div>}
                {r.location && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-cream-dim"><MapPin className="h-3 w-3" /> {r.location}</div>
                )}
                {r.restrictions?.length > 0 && <div className="mt-1.5"><RestrictionBadges restrictions={r.restrictions} /></div>}
                {isMenu && <p className="text-[11px] text-cream-dim mt-1 leading-snug">Menus for the pool-area restaurants and bars.</p>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
